import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosProcedimientos, filtrarProcedimientos } from "@/lib/filtros/procedimientos";

/**
 * Procedimiento es un procedimiento LOTO de un equipo (GET /api/v1/procedimientos). El
 * versionado es documental: `codigo_procedimiento` + `version_procedimiento` identifican
 * una revisión; una revisión nueva es un registro nuevo con el mismo código y otra
 * versión. `pasos` es la lista ORDENADA de pasos (JSONB en el backend).
 */
export type Procedimiento = {
  id: number;
  id_equipo: number;
  codigo_equipo: string;
  nombre_equipo: string;
  codigo_procedimiento: string;
  version_procedimiento: string;
  pasos: string[];
  verificacion_energia_cero: string | null;
  activo: boolean;
  version: number;
  updated_at: string;
};

/** ProcedimientoInput es el payload de alta/edición (POST/PUT /api/v1/procedimientos). */
export type ProcedimientoInput = {
  id_equipo: number;
  codigo_procedimiento: string;
  version_procedimiento: string;
  pasos: string[];
  verificacion_energia_cero: string | null;
  activo: boolean;
};

/**
 * AsignacionMasivaInput clona un procedimiento hacia varios equipos: los `id_equipos`
 * seleccionados, o TODOS los equipos loteables si `todos_loteables` es true.
 */
export type AsignacionMasivaInput = {
  codigo_procedimiento: string;
  version_procedimiento: string;
  pasos: string[];
  verificacion_energia_cero: string | null;
  activo: boolean;
  id_equipos: number[];
  todos_loteables: boolean;
};

/** AsignacionMasivaResult es la respuesta de la asignación masiva (POST …/asignacion-masiva). */
export type AsignacionMasivaResult = {
  creados: number;
  procedimientos: Procedimiento[];
};

/**
 * DisponibilidadProcedimiento es el estado de un procedimiento A EFECTOS DE ETIQUETA (#303),
 * derivado de si se puede ELEGIR (E3/D10), nunca de `activo` a secas: `vigente` es el que está
 * entre los asignables; `retirado` el que no lo está porque el gestor lo desactivó; y
 * `no_disponible` el que no lo está por cualquier otro motivo —lo movieron a otro equipo, lo
 * borraron del catálogo— pero del que SÍ se conoce el código (E2/D8). Cuando no se conoce ni
 * eso, no hay procedimiento que etiquetar: ver `etiquetaProcedimientoAusente`.
 */
export type DisponibilidadProcedimiento = "vigente" | "retirado" | "no_disponible";

// Sufijo con el que se marca cada disponibilidad. `vigente` no lleva ninguno: lo que se puede
// elegir se lee limpio, y así la marca solo aparece cuando hay algo que advertir.
const MARCA: Record<DisponibilidadProcedimiento, string> = {
  vigente: "",
  retirado: " (retirado)",
  no_disponible: " (no disponible)",
};

/**
 * etiquetaProcedimiento es CÓMO SE NOMBRA un procedimiento en toda la app: su código y su
 * versión documental (`PR-200 · v2`), con la marca que le corresponda. Fuente ÚNICA del
 * formato (#303): el alta, la ficha y el cambio de procedimiento lo llaman en vez de repetir
 * la plantilla, para que un cambio de formato se haga en un solo sitio y el técnico lea
 * siempre lo mismo. Es pura: sin hooks ni red.
 *
 * El parámetro es un `Pick` a propósito: la cabecera de la ficha no usa el `Procedimiento` del
 * catálogo sino el bloque que proyecta el servidor (`LotoDetail["procedimiento"]`), y ambos
 * comparten estos dos campos.
 */
export function etiquetaProcedimiento(
  procedimiento: Pick<Procedimiento, "codigo_procedimiento" | "version_procedimiento">,
  disponibilidad: DisponibilidadProcedimiento = "vigente",
): string {
  const { codigo_procedimiento, version_procedimiento } = procedimiento;
  return `${codigo_procedimiento} · v${version_procedimiento}${MARCA[disponibilidad]}`;
}

/**
 * etiquetaProcedimientoAusente nombra el procedimiento del que NO se conoce el código: el
 * formulario nace con un id que su catálogo nunca trajo (caso E5 de #293). Solo queda el id, y
 * ahí «#id (no disponible)» es la mejor verdad disponible, no una degradación (D9). Tiene
 * firma propia porque el dato del que parte es otro (D4): si el código se conoce —aunque el
 * procedimiento haya desaparecido después— se conserva con `etiquetaProcedimiento` (D8).
 */
export function etiquetaProcedimientoAusente(id: number): string {
  return `#${id} (no disponible)`;
}

/**
 * DatosEtiqueta es lo mínimo con lo que se puede NOMBRAR un procedimiento. Se maneja aparte del
 * `Procedimiento` completo a propósito: sobrevive a que el catálogo deje de traerlo, y entonces
 * `activo` sería un dato caducado que nadie debe mirar.
 */
export type DatosEtiqueta = Pick<Procedimiento, "codigo_procedimiento" | "version_procedimiento">;

/**
 * etiquetaProcedimientoNoAsignable nombra el procedimiento que está ELEGIDO pero que NO se puede
 * elegir, con la precedencia ÚNICA de #303 D10 (#309 D3). Quien llama ya ha descartado el paso 1
 * —lo asignable se nombra limpio con `etiquetaProcedimiento`—, así que aquí quedan los otros
 * tres: desactivado en el catálogo ⇒ «(retirado)»; cualquier otro motivo con el código conocido
 * ⇒ «(no disponible)»; y solo cuando nunca se supo cómo se llama, «#id (no disponible)».
 *
 * Las dos pantallas que la usan difieren en UNA cosa, y por eso es un parámetro explícito y no
 * dos copias: de dónde sale el código cuando el catálogo ya no lo trae. En el alta es lo que el
 * formulario RECORDABA (#293 D8); en el diálogo de cambio, el bloque que el SERVIDOR proyecta en
 * la ficha del LOTO, que llega aunque el procedimiento esté dado de baja (#236). El catálogo,
 * cuando lo trae, manda sobre lo conocido: es el dato de ahora, y es el único que sabe si está
 * desactivado. Es pura: sin hooks, sin red y sin tocar lo que recibe.
 */
export function etiquetaProcedimientoNoAsignable(
  id: number,
  enCatalogo: (DatosEtiqueta & Pick<Procedimiento, "activo">) | undefined,
  conocido: DatosEtiqueta | null,
): string {
  const datos = enCatalogo ?? conocido;
  if (datos == null) {
    return etiquetaProcedimientoAusente(id);
  }
  return etiquetaProcedimiento(datos, enCatalogo?.activo === false ? "retirado" : "no_disponible");
}

/**
 * procedimientosQueryKey namespacea la caché de procedimientos POR obra:
 * `["obra", obraId, "procedimientos"]` (F7 #158). Aísla en la caché de Query los
 * procedimientos de una obra de los de otra.
 */
export function procedimientosQueryKey(obraId: number) {
  return ["obra", obraId, "procedimientos"] as const;
}

/**
 * procedimientosQueryOptions lista los procedimientos vivos con su equipo de la obra
 * `obraId`. Se refresca invalidando la clave tras cada mutación (create/update/delete/
 * asignación masiva), igual que el resto de maestros de F2, ahora con la obra en la ruta
 * y en la clave.
 */
export function procedimientosQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: procedimientosQueryKey(obraId),
    queryFn: () => api.get<Procedimiento[]>(`/obras/${obraId}/procedimientos`),
  });
}

/**
 * useProcedimientos lista los procedimientos de la obra, opcionalmente FILTRADOS (#247):
 * con red filtra el servidor; sin red, la función pura sobre la lista base cacheada.
 */
export function useProcedimientos(obraId: number, filtros: FiltrosProcedimientos = {}) {
  return useListaFiltrada<Procedimiento>({
    claveBase: procedimientosQueryKey(obraId),
    url: `/obras/${obraId}/procedimientos`,
    filtros,
    filtrar: filtrarProcedimientos,
  });
}

export function useCreateProcedimiento(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProcedimientoInput) =>
      api.post<Procedimiento>(`/obras/${obraId}/procedimientos`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: procedimientosQueryKey(obraId) }),
  });
}

export function useUpdateProcedimiento(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ProcedimientoInput }) =>
      api.put<Procedimiento>(`/obras/${obraId}/procedimientos/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: procedimientosQueryKey(obraId) }),
  });
}

export function useDeleteProcedimiento(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${obraId}/procedimientos/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: procedimientosQueryKey(obraId) }),
  });
}

export function useAsignacionMasiva(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AsignacionMasivaInput) =>
      api.post<AsignacionMasivaResult>(`/obras/${obraId}/procedimientos/asignacion-masiva`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: procedimientosQueryKey(obraId) }),
  });
}
